/**
 * Tamanhos de fonte em px
 * xs: 10px, sm: 12px, md: 14px, lg: 16px, xl: 20px, 2xl: 24px, 3xl: 28px, 4xl: 36px
 */

const fonts = {
  inter: {
    thin: 'Inter_100Thin',
    regular: 'Inter_400Regular',
    bold: 'Inter_700Bold',
    black: 'Inter_900Black',
  },
  system: {
    regular: 'System',
    bold: 'System',
    medium: 'System',
    light: 'System',
    thin: 'System',
    black: 'System',
  },
};

const headingSizes = {
  h1: 0.8421,
  h2: 0.8181,
  h3: 0.7777,
  h4: 0.7142,
  h5: 0.75,
  h6: 0.8,
};

const fontSizes = {
  large: 2.25,
  medium: 1.75,
  small: 1.25,
  body: 1.125,
  paragraph: 1,
  text: 0.875,
  mini: 0.75,
  xs: 0.625,
};

/**
 * Radii tokens (borderRadius) - Gluestack padrão
 */
export const radii = {
  none: 0,
  xs: 2,
  sm: 4,
  md: 6,
  lg: 8,
  xl: 12,
  '2xl': 16,
  '3xl': 24,
  full: 9999,
} as const;

export const breakpoints = {
  base: 0,
  sm: 480,
  md: 768,
  lg: 992,
  xl: 1280,
} as const;

/**
 * Media queries tokens - Gluestack padrão
 */
export const mediaQueries = {
  base: '@media screen and (min-width: 0)',
  xs: '@media screen and (min-width: 400px)',
  sm: '@media screen and (min-width: 480px)',
  md: '@media screen and (min-width: 768px)',
  lg: '@media screen and (min-width: 992px)',
  xl: '@media screen and (min-width: 1280px)',
} as const;

/**
 * Opacity tokens - Gluestack padrão
 */
export const opacity = {
  0: 0,
  5: 0.05,
  10: 0.1,
  20: 0.2,
  25: 0.25,
  30: 0.3,
  40: 0.4,
  50: 0.5,
  60: 0.6,
  70: 0.7,
  75: 0.75,
  80: 0.8,
  90: 0.9,
  95: 0.95,
  100: 1,
} as const;

/**
 * Space tokens - Gluestack padrão (completo)
 */
export const space = {
  px: '1px',
  '0': 0,
  '0.5': 2,
  '1': 4,
  '1.5': 6,
  '2': 8,
  '2.5': 10,
  '3': 12,
  '3.5': 14,
  '4': 16,
  '4.5': 18,
  '5': 20,
  '6': 24,
  '7': 28,
  '8': 32,
  '9': 36,
  '10': 40,
  '11': 44,
  '12': 48,
  '16': 64,
  '20': 80,
  '24': 96,
  '32': 128,
  '40': 160,
  '48': 192,
  '56': 224,
  '64': 256,
  '72': 288,
  '80': 320,
  '96': 384,
  '1/2': '50%',
  '1/3': '33.333%',
  '2/3': '66.666%',
  '1/4': '25%',
  '2/4': '50%',
  '3/4': '75%',
  '1/5': '20%',
  '2/5': '40%',
  '3/5': '60%',
  '4/5': '80%',
  '1/6': '16.666%',
  '2/6': '33.333%',
  '3/6': '50%',
  '4/6': '66.666%',
  '5/6': '83.333%',
  full: '100%',
} as const;

export const fontWeights = {
  hairline: '100',
  thin: '200',
  light: '300',
  normal: '400',
  medium: '500',
  semibold: '600',
  bold: '700',
  extrabold: '800',
  black: '900',
} as const;

/**
 * Line heights tokens - Gluestack padrão
 */
export const lineHeightsPx = {
  '2xs': 12,
  xs: 16,
  sm: 20,
  md: 24,
  lg: 28,
  xl: 32,
  '2xl': 36,
  '3xl': 40,
  '4xl': 48,
  '5xl': 56,
  '6xl': 64,
} as const;

/**
 * Letter spacings tokens - Gluestack padrão
 */
export const letterSpacings = {
  xs: -0.4,
  sm: -0.2,
  md: 0,
  lg: 0.2,
  xl: 0.4,
  '2xl': 1.6,
} as const;

/**
 * Z-Indices tokens - Gluestack padrão
 */
export const zIndices = {
  hide: -1,
  auto: 'auto',
  base: 0,
  docked: 10,
  dropdown: 1000,
  sticky: 1100,
  banner: 1200,
  overlay: 1300,
  modal: 1400,
  popover: 1500,
  skipLink: 1600,
  toast: 1700,
  tooltip: 1800,
} as const;

/**
 * Border widths tokens - Gluestack padrão
 */
export const borderWidths = {
  none: 0,
  hairline: 1,
  thin: 2,
  thick: 4,
  heavy: 8,
} as const;

export const fontSizesPx = {
  '2xs': 10,
  xs: 12,
  sm: 14,
  md: 16,
  lg: 18,
  xl: 20,
  '2xl': 24,
  '3xl': 30,
  '4xl': 36,
  '5xl': 48,
  '6xl': 60,
  '7xl': 72,
  '8xl': 96,
  '9xl': 128,
} as const;

export const borderRadius = {
  xs: 2,
  sm: 4,
  md: 6,
  lg: 8,
  xl: 12,
  '2xl': 16,
  '3xl': 24,
  '4xl': 32,
  none: 0,
  full: 9999,
};

const lineHeights = {
  normal: 'normal',
  none: 1,
  shorter: 1.25,
  short: 1.375,
  base: 1.5,
  tall: 1.625,
  taller: 2,
  '3': 0.75,
  '4': 1,
  '5': 1.25,
  '6': 1.5,
  '7': 1.75,
  '8': 2,
  '9': 2.25,
  '10': 2.5,
};

const colors = {
  primary: {
    '100': '#C8DDFF',
    '200': '#A7B3FF',
    '300': '#7A8CFF',
    '400': '#4E66FF',
    '500': '#2240FF',
    '600': '#182DB3',
  },
  secondary: {
    '100': '#E2FFF2',
    '200': '#C5FFE4',
    '300': '#A7FFD7',
    '400': '#8AFFC9',
    '500': '#6DFFBC',
    '600': '#4CB384',
  },
  success: {
    '600': '#115B43',
    '500': '#1B6E53',
    '400': '#009262',
    '300': '#32C997',
    '200': '#84DFC1',
    '100': '#D1DBF8',
  },
  warning: {
    '600': '#E48900',
    '500': '#FFA826',
    '400': '#FFB240',
    '300': '#FFC670',
    '200': '#FFD596',
    '100': '#FFF8EC',
  },
  error: {
    '700': '#910808',
    '600': '#C33025',
    '500': '#E01507',
    '400': '#E02B1D',
    '300': '#FF5A4F',
    '200': '#F0857D',
    '100': '#FFF1F0',
  },
  gray: {
    '700': '#7D7D7D',
    '600': '#555555',
    '500': '#676767',
    '400': '#888888',
    '300': '#E4E4E4',
    '200': '#B5B5B5',
    '100': '#E2E2E2',
  },
  base: {
    white: '#FFFFFF',
    gray: '#808191',
    silver: 'F5F7FA',
    blue: '#2240FF',
    darkBlue: '#070D31',
    green: '#6DFFBC',
    button: '#FF7A8A',
    black: '#000000',
    backgroundTab: '#252836',
    purple: '#839CFF',
  },
};

const gradient_colors = {
  primary: ['#153c8e', '#194173', '#183954', '#142c44'],
  secondary: ['#19285e', '#1e2c5e', '#293360', '#353d60'],
};

const spacing = {
  s1: '4px',
  s2: '8px',
  s3: '12px',
  s4: '16px',
  s5: '20px',
  s6: '24px',
  s8: '32px',
  s10: '40px',
  s12: '48px',
  s16: '64px',
  s20: '80px',
  s24: '96px',
};

export default {
  fonts,
  fontSizes,
  lineHeights,
  letterSpacings,
  headingSizes,
  colors,
  spacing,
  gradient_colors,
  fontSizesPx,
  borderRadius,
  radii,
  mediaQueries,
  opacity,
  space,
  lineHeightsPx,
  zIndices,
  borderWidths,
};
